import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getCreateChannelFormDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-channel.form-default-values';
import { CreateChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-channel.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoCreateChannelMutation } from '@service/cpms-bo-charge-point.service';

import {
  CreateChargePointChannelContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelCreateFormController } from './charge-point-channel-create-form/charge-point-channel-create-form.controller';

interface ChargePointChannelCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelCreateController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createChannelMutation] = useCpmsBoCreateChannelMutation();
  const chargePoint = useChargePointContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<CreateChannelModel, 'chargePointId' | 'channel'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-create.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<CreateChannelModel, 'chargePointId' | 'channel'>
  > = (createChannel) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const ids = new Set(
      chargePoint.channels.flatMap((channel) =>
        channel.physicalSockets.map((socket) => socket.ocppConnectorId),
      ),
    );
    let newOcppConnectorId = 1;

    createChannelMutation({
      chargePointId: createChannel.chargePointId,
      channel: {
        ...createChannel.channel,
        physicalSockets: createChannel.channel.physicalSockets?.map(
          (socket) => {
            while (ids.has(newOcppConnectorId)) newOcppConnectorId += 1;
            ids.add(newOcppConnectorId);

            return {
              ...socket,
              ocppConnectorId: newOcppConnectorId,
            };
          },
        ),
      },
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-channel-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-create.error', { error: errorMessage }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getCreateChannelFormDefaultValues(chargePoint.id);

  return (
    <CreateChargePointChannelContextProvider data={data}>
      <ChargePointChannelCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateChargePointChannelContextProvider>
  );
};
